import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import type { SnapshotId } from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { FormInputError } from 'components/FormInputError/FormInputError';
import { SelectSnapshot } from 'components/SelectSnapshot/SelectSnapshot';
import { useLocale } from 'containers/App/LocaleContext';
import type { Snapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { useMemo, useState } from 'react';
import type { FC } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';

type ChangesSelectSnapshotModel = {
  before: SnapshotId | null;
  setBefore: (s: SnapshotId | null) => void;
  beforeNotReady: boolean;
  after: SnapshotId | null;
  setAfter: (s: SnapshotId | null) => void;
  afterNotReady: boolean;
  snapshots: Snapshot[];
};

export const useChangesSelectSnapshot = (): ChangesSelectSnapshotModel => {
  const snapshots = useSnapshots();

  const isSnapshotNotReady = (snapshot: Snapshot) =>
    snapshot.status !== 'done' || snapshot.running;

  /**
   * Identify a snapshot that is selected but somehow invalid for comparison
   *
   * We want to show such snapshots (in progress etc.) in the UI, but accompany by
   * error message and not allow to submit the form.
   */
  const isSnapshotIdNotReady = (snapshotId: SnapshotId | null) => {
    if (snapshotId === null) {
      return false;
    }
    const snapshot = snapshots.find((s) => s.id === snapshotId);
    return !snapshot || isSnapshotNotReady(snapshot);
  };

  const useSnapshotNotReady = (snapshotId: SnapshotId | null): boolean =>
    useMemo(
      () => isSnapshotIdNotReady(snapshotId),
      [snapshotId], // no need to recalculate ever, used as default state value
    );

  const activeSnapshotId = useSnapshotId();
  const [before, setBefore] = useState<SnapshotId | null>(activeSnapshotId);

  const [after, setAfter] = useState<SnapshotId | null>(
    () =>
      snapshots.find((s) => s.id !== activeSnapshotId && !isSnapshotNotReady(s))
        ?.id ?? null,
  );

  const beforeNotReady = useSnapshotNotReady(before);
  const afterNotReady = useSnapshotNotReady(after);

  return {
    before,
    setBefore: (s: SnapshotId | null) => {
      setBefore(s);
      if (s === after) {
        setAfter(null);
      }
    },
    beforeNotReady,
    after,
    setAfter,
    afterNotReady,
    snapshots,
  };
};

interface Props {
  className?: string;
  model: ChangesSelectSnapshotModel;
  onSubmit: () => Awaitable<void>;
}

export const ChangesSelectSnapshots: FC<Props> = (props) => {
  const snapshotSelectionMsg = useLocale().managementChanges.snapshotSelection;

  const notEnoughSnapshots = props.model.snapshots.length < 2;

  const notReadyError =
    (props.model.beforeNotReady || props.model.afterNotReady) &&
    !notEnoughSnapshots
      ? snapshotSelectionMsg.snapshotNotReady
      : undefined;

  const selectPlaceholder = notEnoughSnapshots
    ? snapshotSelectionMsg.notEnoughSnapshots.placeholder
    : undefined;

  return (
    <>
      {props.model.snapshots.length < 2 && (
        <div className='flex flex-row gap-1'>
          <Icon
            glyph='fa-circle-info'
            iconStyle='solid'
            className='text-red-600'
          />
          <div>
            <div className='font-bold'>
              {snapshotSelectionMsg.notEnoughSnapshots.title}
            </div>
            <div>{snapshotSelectionMsg.notEnoughSnapshots.description}</div>
          </div>
        </div>
      )}
      <div
        className={classNames('flex flex-row items-end gap-4', props.className)}
      >
        <div className='w-80'>
          <div className='form__label'>{snapshotSelectionMsg.beforeLabel}</div>
          <SelectSnapshot
            disabled={notEnoughSnapshots}
            snapshots={props.model.snapshots}
            value={props.model.before}
            onChange={props.model.setBefore}
            placeholder={selectPlaceholder}
          />
        </div>
        <div className='w-80'>
          <div className='form__label'>{snapshotSelectionMsg.afterLabel}</div>
          <SelectSnapshot
            disabled={notEnoughSnapshots}
            snapshots={props.model.snapshots}
            value={props.model.after}
            onChange={props.model.setAfter}
            placeholder={selectPlaceholder}
            isOptionDisabled={(snapshot) => snapshot.id === props.model.before}
          />
        </div>
        <Button
          disabled={
            props.model.before === null ||
            props.model.after === null ||
            props.model.beforeNotReady ||
            props.model.afterNotReady
          }
          size='base'
          type='button'
          variant='primary'
          onClick={props.onSubmit}
        >
          {snapshotSelectionMsg.submitButtonTitle}
        </Button>
      </div>
      <FormInputError error={notReadyError} />
    </>
  );
};
